/* =====================================================================
 * SidebarItem — 개별 대화 항목
 * ===================================================================== */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { MessageSquare, MoreHorizontal, Pencil, Trash2, Pin, Check, X } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { DropdownMenu } from '../common/DropdownMenu';
import type { Conversation } from '@auw/workspace-utils';
import { WORKSPACE_MODES } from '@auw/workspace-utils';
import { workspaceModeToAgentProfileId } from '@auw/i18n';
import { updateItem } from '@auw/api-client';
import { prefetchChatRenderModules } from '../../lib/chatRenderPrefetch';
import { markHistoryItemClick } from '@auw/workspace-utils';
import { buildConversationSettings } from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrapper = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sidebar.historyGap};
  padding: ${({ theme }) => theme.sidebar.historyPad};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};

    .more-btn {
      opacity: 1;
    }
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.bgHover};
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.sidebarMobile.historyGap};
    padding: ${({ theme }) => theme.sidebarMobile.historyPad};
  }
`;

const Icon = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${Wrapper}:hover & {
    transform: scale(1.12) rotate(-6deg);
  }
`;

/** 채팅 히스토리용 알파벳 아바타 (원형 + 우하단 도트) */
const ModeAvatarWrap = styled.span`
  position: relative;
  width: 20px;
  height: 20px;
  min-width: 20px;
  flex-shrink: 0;
`;

const ModeAvatarCircle = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 3px rgba(0,0,0,0.35)'
      : '0 1px 3px rgba(0,0,0,0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e'};
  font-weight: 700;
  font-size: 9px;
  line-height: 1;
  user-select: none;
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275),
              box-shadow 0.3s ease;

  ${Wrapper}:hover & {
    transform: scale(1.12) rotate(-6deg);
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 2px 6px rgba(0,0,0,0.45)'
        : '0 2px 6px rgba(0,0,0,0.12)'};
  }

  svg { color: inherit; flex-shrink: 0; }
`;

const ModeAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

const ModeAvatarLogoImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: contain;
  padding: 2px;
`;

const ModeAvatarDot = styled.span<{ $color: string }>`
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 1.5px solid ${({ theme }) => theme.colors.bgSidebar};
`;

const Title = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.sidebar.historyFontSize};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sidebarMobile.historyFontSize};
  }
`;

const RenameInput = styled.input`
  flex: 1;
  font-size: ${({ theme }) => theme.sidebar.historyFontSize};
  padding: 2px 4px;
  background: ${({ theme }) => theme.colors.bgInput};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sidebarMobile.historyFontSize};
  }
`;

const MoreBtn = styled.button`
  padding: 3px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast},
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const RenameActions = styled.div`
  display: flex;
  gap: 2px;
`;

const ActionBtn = styled.button`
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface Props {
  conversation: Conversation;
  isActive: boolean;
}

type ExternalAppBrand = {
  src: string;
  alt: string;
  dotColor: string;
};

function getExternalAppBrand(settings: Record<string, unknown> | undefined): ExternalAppBrand | null {
  if (!settings || typeof settings !== 'object') return null;

  const messagingBridge = settings.messagingBridge;
  if (messagingBridge && typeof messagingBridge === 'object') {
    const provider = String((messagingBridge as Record<string, unknown>).provider || '').trim().toLowerCase();
    if (provider === 'slack') {
      return { src: '/logo/slack.png', alt: 'Slack', dotColor: '#4A154B' };
    }
    if (provider === 'discord') {
      return { src: '/logo/discord.png', alt: 'Discord', dotColor: '#5865F2' };
    }
  }

  if (settings.slackBridge && typeof settings.slackBridge === 'object') {
    return { src: '/logo/slack.png', alt: 'Slack', dotColor: '#4A154B' };
  }
  if (settings.discordBridge && typeof settings.discordBridge === 'object') {
    return { src: '/logo/discord.png', alt: 'Discord', dotColor: '#5865F2' };
  }

  return null;
}

const SidebarItemImpl: React.FC<Props> = ({ conversation, isActive }) => {
  const { t } = useTranslation('common');
  const setActive = useChatStore((s) => s.setActiveConversation);
  const deleteConv = useChatStore((s) => s.deleteConversation);
  const renameConv = useChatStore((s) => s.renameConversation);
  const addToast = useChatStore((s) => s.addToast);
  const prefetchMessages = useChatStore((s) => s.prefetchMessages);
  const agentPictures: Record<string, string | null> = {};
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(conversation.title);
  const [modeAvatarError, setModeAvatarError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
    direction: 'up' | 'down';
  }>({ open: false, x: 0, y: 0, direction: 'down' });

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const modeProfileId = conversation.mode
    ? workspaceModeToAgentProfileId(conversation.mode)
    : null;
  const modePicture = modeProfileId ? (agentPictures[modeProfileId] ?? null) : null;
  const externalAppBrand = conversation.mode === 'agent'
    ? getExternalAppBrand(conversation.workspaceSettings)
    : null;

  useEffect(() => {
    setModeAvatarError(false);
  }, [conversation.id, modePicture, externalAppBrand?.src]);

  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (isActive || conversation.historyLoaded) return;
    hoverTimer.current = setTimeout(() => {
      prefetchChatRenderModules();
      prefetchMessages(conversation.id);
    }, 200);
  }, [isActive, conversation.id, conversation.historyLoaded, prefetchMessages]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  useEffect(() => handleMouseLeave, [handleMouseLeave]);

  const handleClick = () => {
    if (!editing) {
      markHistoryItemClick(conversation.id);
      setActive(conversation.id);
    }
  };

  const commitRename = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== conversation.title) {
      renameConv(conversation.id, trimmed);
    }
    setEditing(false);
  };

  const cancelRename = () => {
    setDraft(conversation.title);
    setEditing(false);
  };

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const direction: 'up' | 'down' = rect.bottom > window.innerHeight * 0.6 ? 'up' : 'down';
    const y = direction === 'up' ? rect.top : rect.bottom + 4;
    setMenu({ open: true, x: rect.right, y, direction });
  };

  const setConversationLocal = (next: Conversation) => {
    useChatStore.setState((s) => ({
      conversations: {
        ...s.conversations,
        [conversation.id]: next,
      },
    }));
  };

  const handlePin = async () => {
    if (conversation.pinnedAt) return;
    const next: Conversation = {
      ...conversation,
      pinnedAt: new Date().toISOString(),
    };
    setConversationLocal(next);
    try {
      await updateItem(conversation.id, {
        settings: buildConversationSettings(next),
      });
      addToast({
        type: 'success',
        title: t('toast.pinSuccess'),
        description: t('toast.pinSuccessDesc'),
      });
    } catch {
      setConversationLocal(conversation);
      addToast({
        type: 'error',
        title: t('toast.pinFail'),
        description: t('toast.retryLater'),
      });
    }
  };

  const handleUnpin = async () => {
    if (!conversation.pinnedAt) return;
    const next: Conversation = {
      ...conversation,
      pinnedAt: null,
    };
    setConversationLocal(next);
    try {
      await updateItem(conversation.id, {
        settings: buildConversationSettings(next),
      });
      addToast({
        type: 'success',
        title: t('toast.unpinSuccess'),
        description: t('toast.unpinSuccessDesc'),
      });
    } catch {
      setConversationLocal(conversation);
      addToast({
        type: 'error',
        title: t('toast.unpinFail'),
        description: t('toast.retryLater'),
      });
    }
  };

  const menuItems = [
    {
      id: 'rename',
      label: t('tooltip.rename'),
      icon: <Pencil size={14} />,
      onClick: () => {
        setDraft(conversation.title);
        setEditing(true);
      },
    },
    {
      id: 'delete',
      label: t('button.delete'),
      icon: <Trash2 size={14} />,
      danger: true,
      onClick: () => deleteConv(conversation.id),
    },
    ...(conversation.pinnedAt
      ? [
          {
            id: 'unpin',
            label: t('tooltip.unpin'),
            icon: <Pin size={14} />,
            onClick: handleUnpin,
          },
        ]
      : [
          {
            id: 'pin',
            label: t('tooltip.pin'),
            icon: <Pin size={14} />,
            onClick: handlePin,
          },
        ]),
  ];

  return (
    <>
      <Wrapper
        $active={isActive}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Icon>
          {(() => {
            const modeInfo = conversation.mode ? WORKSPACE_MODES[conversation.mode] : null;
            if (modeInfo) {
              return (
                <ModeAvatarWrap>
                  <ModeAvatarCircle>
                    {externalAppBrand && !modeAvatarError ? (
                      <ModeAvatarLogoImage
                        src={externalAppBrand.src}
                        alt={externalAppBrand.alt}
                        onError={() => setModeAvatarError(true)}
                      />
                    ) : modePicture && !modeAvatarError ? (
                      <ModeAvatarImage
                        src={modePicture}
                        alt={t(modeInfo.labelKey)}
                        referrerPolicy="no-referrer"
                        onError={() => setModeAvatarError(true)}
                      />
                    ) : (
                      modeInfo.letter
                    )}
                  </ModeAvatarCircle>
                  <ModeAvatarDot $color={externalAppBrand?.dotColor ?? modeInfo.color} />
                </ModeAvatarWrap>
              );
            }
            return <MessageSquare size={16} />;
          })()}
        </Icon>

        {editing ? (
          <>
            <RenameInput
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') cancelRename();
              }}
              onBlur={commitRename}
              onClick={(e) => e.stopPropagation()}
            />
            <RenameActions>
              <ActionBtn onClick={(e) => { e.stopPropagation(); commitRename(); }}>
                <Check size={14} />
              </ActionBtn>
              <ActionBtn onClick={(e) => { e.stopPropagation(); cancelRename(); }}>
                <X size={14} />
              </ActionBtn>
            </RenameActions>
          </>
        ) : (
          <>
            <Title>{conversation.title}</Title>
            <MoreBtn
              className="more-btn"
              onClick={handleMoreClick}
              title={t('tooltip.moreActions')}
            >
              <MoreHorizontal size={14} />
            </MoreBtn>
          </>
        )}
      </Wrapper>

      <DropdownMenu
        open={menu.open}
        x={menu.x}
        y={menu.y}
        direction={menu.direction}
        align="right"
        minWidth={140}
        onClose={() => setMenu((prev) => ({ ...prev, open: false }))}
        items={menuItems}
      />
    </>
  );
};

export const SidebarItem = React.memo(
  SidebarItemImpl,
  (prev, next) => prev.isActive === next.isActive && prev.conversation === next.conversation,
);
SidebarItem.displayName = 'SidebarItem';
